import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MeditationResults, MeditationResultItem } from '../components/meditation';
import { config } from '../config';
import { authService } from '../services/auth';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import SEO from '../components/common/SEO';

const AVAILABLE_TAGS = [
  'Sitting', 'Lying', 'Walking', 'Eyes open', 'Eyes closed', 'Relaxing',
  'Thinking', 'Focus', 'Sleeping', 'Dreaming', 'Out of body', 'Spiritual', 'Nature',
  'Non-Spiritual', 'Body Scan', 'Story', 'Manifestation', 'Visualization',
  'Affirmations', 'Mantras', 'Healing', 'Music','Ambient','Instrumental',
  'Delta', 'Theta', 'Alpha', 'Beta','Gamma', 
  'Binaural Beats', 'Amplitude Modulation', 'Frequency Modulation',
  'Stereo Modulation', 'Isochronic Tones'
];

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: white;
`;

const SearchContainer = styled.div`
  margin-bottom: 40px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 15px;
  font-size: 1.1rem;
  border: 1px solid #666;
  border-radius: 8px;
  background-color: #333;
  color: #cccccc;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Label = styled.label`
  color: #666;
  font-size: 0.9rem;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #666;
  border-radius: 4px;
  background-color: #333;
  color: #cccccc;
  font-size: 0.9rem;
`;


const TagsSection = styled.div`
  margin-top: 20px;
  margin-bottom: 30px;
`;

const TagsLabel = styled.h3`
  color: #ffffff;
  margin-bottom: 12px;
  font-size: 1rem;
  font-weight: 600;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.button<{ $selected: boolean }>`
  background: ${props => props.$selected ? '#007bff' : '#333'};
  color: white;
  border: 1px solid ${props => props.$selected ? '#007bff' : '#444'};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$selected ? '#0056b3' : '#444'};
  }
`;

const SearchButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 20px;

  &:hover {
    background: #45a049;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const ResultsSection = styled.div`
  margin-top: 40px;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const InfiniteScrollSentinel = styled.div`
  height: 20px;
  margin: 20px 0;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1rem;
`;

const EndStateContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  margin: 40px 0;
`;

const EndStateTitle = styled.h3`
  color: #ffffff;
  font-size: 1.2rem;
  margin-bottom: 12px;
`;

const EndStateMessage = styled.p`
  color: #cccccc;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const RefineSearchButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #0056b3;
  }
`;

const ResultsCount = styled.div`
  color: #999;
  font-size: 0.9rem;
  margin-bottom: 20px;
  text-align: center;
`;

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [type, setType] = useState('all');
  const [duration, setDuration] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<MeditationResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [maxResults] = useState(100);
  const [offset, setOffset] = useState(0);

  // Parse URL parameters on mount and restore search state
  useEffect(() => {
    const keyword = searchParams.get('keyword') || '';
    const urlType = searchParams.get('type') || 'all';
    const urlDuration = searchParams.get('duration') || 'all';
    const urlSort = searchParams.get('sort') || 'date_desc';
    const urlTags = searchParams.get('tags');
    const urlTagsArray = urlTags ? urlTags.split(',').filter(tag => AVAILABLE_TAGS.includes(tag)) : [];
    const urlLoaded = parseInt(searchParams.get('loaded') || '0', 10);

    setSearchQuery(keyword);
    setType(urlType);
    setDuration(urlDuration);
    setSortBy(urlSort);
    setSelectedTags(urlTagsArray);

    // If we have search parameters in the URL, perform the search automatically
    if (keyword || urlType !== 'all' || urlDuration !== 'all' || urlTagsArray.length > 0) {
      setHasSearched(true);
      if (urlLoaded > 0) {
        // Restore previous search results by loading all batches up to urlLoaded
        restoreSearchResults(keyword, urlType, urlDuration, urlTagsArray, urlLoaded, urlSort);
      } else {
        // Start fresh search
        performInitialSearch(keyword, urlType, urlDuration, urlSort, urlTagsArray);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Helper function to build API parameters
  const buildApiParams = (keyword: string, searchType: string, searchDuration: string, sortOrder: string, tags: string[], currentOffset: number) => {
    const apiParams = new URLSearchParams();
    
    if (keyword.trim()) {
      apiParams.append('keyword', keyword.trim());
    }
    
    if (searchType !== 'all') {
      apiParams.append('is_unguided', searchType === 'unguided' ? 'true' : 'false');
    }
    
    if (searchDuration !== 'all') {
      apiParams.append('duration', searchDuration);
    }
    
    if (sortOrder !== 'date_desc') {
      apiParams.append('sort', sortOrder);
    }
    
    if (tags.length > 0) {
      apiParams.append('tags', tags.join(','));
    }

    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');

    return apiParams;
  };

  // Function to update URL parameters
  const updateURLParams = (keyword: string, searchType: string, searchDuration: string, sortOrder: string, tags: string[], loaded?: number) => {
    const newParams = new URLSearchParams();
    
    if (keyword.trim()) {
      newParams.set('keyword', keyword.trim());
    }
    
    if (searchType !== 'all') {
      newParams.set('type', searchType);
    }
    
    if (searchDuration !== 'all') {
      newParams.set('duration', searchDuration);
    }
    
    if (sortOrder !== 'date_desc') {
      newParams.set('sort', sortOrder);
    }
    
    if (tags.length > 0) {
      newParams.set('tags', tags.join(','));
    }

    if (loaded && loaded > 0) {
      newParams.set('loaded', loaded.toString());
    }

    setSearchParams(newParams);
  };

  // Initial search function (resets results)
  const performInitialSearch = useCallback(async (keyword: string, searchType: string, searchDuration: string, sortOrder: string, tags: string[]) => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);
    setHasMore(true);

    try {
      const apiParams = buildApiParams(keyword, searchType, searchDuration, sortOrder, tags, 0);
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(data.hasMore && meditations.length < maxResults);

      // Update URL with search results
      updateURLParams(keyword, searchType, searchDuration, sortOrder, tags, meditations.length);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults, setSearchParams]);

  // Load more results function for infinite scroll
  const loadMoreResults = useCallback(async () => {
    if (loading || !hasMore || totalLoaded >= maxResults) return;

    setLoading(true);

    try {
      const apiParams = buildApiParams(searchQuery, type, duration, sortBy, selectedTags, offset);
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const newMeditations = data.meditations || [];
      const newTotalLoaded = totalLoaded + newMeditations.length;
      
      setSearchResults(prev => [...prev, ...newMeditations]);
      setOffset(prev => prev + newMeditations.length);
      setTotalLoaded(newTotalLoaded);
      setHasMore(data.hasMore && newTotalLoaded < maxResults);

      // Update URL with new loaded count
      updateURLParams(searchQuery, type, duration, sortBy, selectedTags, newTotalLoaded);
    } catch (error) {
      console.error('Load more error:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, totalLoaded, maxResults, offset, searchQuery, type, duration, selectedTags]);

  // Restore search results when returning from a meditation page
  const restoreSearchResults = useCallback(async (keyword: string, searchType: string, searchDuration: string, tags: string[], targetLoaded: number, sortOrder: string = 'date_desc') => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);

    try {
      // Load all results up to targetLoaded in one request
      const apiParams = buildApiParams(keyword, searchType, searchDuration, sortOrder, tags, 0);
      apiParams.set('limit', Math.min(targetLoaded, maxResults).toString());
      
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(data.hasMore && meditations.length < maxResults);
    } catch (error) {
      console.error('Restore search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults]);

  const performSearch = async () => {
    // Validate at least one search parameter
    if (!searchQuery.trim() && selectedTags.length === 0 && type === 'all' && duration === 'all') {
      alert('Please enter at least one search criteria');
      return;
    }

    setHasSearched(true);
    await performInitialSearch(searchQuery, type, duration, sortBy, selectedTags);
  };

  // Set up infinite scroll
  const { sentinelRef, loadingRef } = useInfiniteScroll({
    loadMore: loadMoreResults,
    hasMore: hasMore && totalLoaded < maxResults,
    loading: loading
  });

  // Create dynamic title and description based on search parameters
  const getPageTitle = () => {
    if (!hasSearched && !searchQuery && selectedTags.length === 0) {
      return 'Search Meditations - YouMeditation';
    }
    
    let parts = [];
    if (searchQuery) parts.push(`"${searchQuery}"`);
    if (selectedTags.length > 0) parts.push(selectedTags.slice(0, 2).join(', '));
    
    const searchText = parts.length > 0 ? parts.join(' ') : 'Meditations';
    return `Search Results for ${searchText} - YouMeditation`;
  };

  const getPageDescription = () => {
    if (!hasSearched && !searchQuery && selectedTags.length === 0) {
      return 'Search and discover meditation scripts on YouMeditation. Filter by keywords, tags, duration, and type to find your perfect meditation experience.';
    }
    
    let searchText = 'meditations';
    if (searchQuery) searchText = `"${searchQuery}" meditations`;
    if (selectedTags.length > 0) searchText += ` in ${selectedTags.slice(0, 2).join(', ')}`;
    
    return `Found ${searchResults.length} ${searchText}. Discover guided and unguided meditation scripts tailored to your preferences.`;
  };

  const getPageKeywords = () => {
    const baseKeywords = 'meditation search, find meditations, meditation filter, YouMeditation search';
    let keywords = [baseKeywords];
    
    if (searchQuery) keywords.push(searchQuery.toLowerCase());
    if (selectedTags.length > 0) keywords.push(selectedTags.join(', ').toLowerCase());
    
    return keywords.join(', ');
  };

  return (
    <>
      <SEO 
        title={getPageTitle()}
        description={getPageDescription()}
        keywords={getPageKeywords()}
        url="/search"
        noIndex={hasSearched && searchResults.length === 0} // Don't index empty search results
      />
      <Container>
      <Title>Search Meditations</Title>
      <SearchContainer>
        <SearchRow>
          <SearchInput
            type="text"
            placeholder="Search by keyword..."
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={(e) => e.key === 'Enter' && performSearch()}
          />

          <Filters>
            <FilterGroup>
              <Label>Type:</Label>
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="all">All Types</option>
                <option value="guided">Guided</option>
                <option value="unguided">Unguided</option>
              </Select>
            </FilterGroup>

            <FilterGroup>
              <Label>Duration:</Label>
              <Select value={duration} onChange={(e) => setDuration(e.target.value)}>
                <option value="all">Any Duration</option>
                <option value="short">Short (0-10 min)</option>
                <option value="medium">Medium (10-20 min)</option>
                <option value="long">Long (20+ min)</option>
              </Select>
            </FilterGroup>

            <FilterGroup>
              <Label>Sort By:</Label>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="date_desc">Date 📉</option>
                <option value="date_asc">Date 📈</option>
                <option value="views_desc">Views 📉</option>
                <option value="views_asc">Views 📈</option>
                <option value="ratings_desc">Ratings 📉</option>
                <option value="ratings_asc">Ratings 📈</option>
              </Select>
            </FilterGroup>
          </Filters>
        </SearchRow>

        <TagsSection>
          <TagsLabel>Tags (select any that apply):</TagsLabel>
          <TagsContainer>
            {AVAILABLE_TAGS.map(tag => (
              <Tag
                key={tag}
                type="button"
                $selected={selectedTags.includes(tag)}
                onClick={() => handleTagToggle(tag)}
              >
                {tag}
              </Tag>
            ))}
          </TagsContainer>
        </TagsSection>

        <SearchButton onClick={performSearch} disabled={loading}>
          {loading ? 'Searching...' : 'Search Meditations'}
        </SearchButton>
      </SearchContainer>

      <ResultsSection>
        {!hasSearched ? (
          <NoResultsMessage>
            Enter search criteria above to find meditations
          </NoResultsMessage>
        ) : (
          <>
            {/* {totalLoaded > 0 && (
              <ResultsCount>
                Showing {totalLoaded} of {totalLoaded >= maxResults ? `${maxResults}+` : 'available'} results
              </ResultsCount>
            )} */}
            
            <MeditationResults 
              meditations={searchResults} 
              loading={false} // Don't show loading state in results grid
            />

            {/* Infinite scroll loading indicator */}
            {loading && searchResults.length > 0 && (
              <LoadingIndicator ref={loadingRef}>
                Loading more meditations...
              </LoadingIndicator>
            )}

            {/* Infinite scroll sentinel */}
            {hasMore && totalLoaded < maxResults && !loading && (
              <InfiniteScrollSentinel ref={sentinelRef} />
            )}

            {/* End state when hitting 100 result limit */}
            {/* {totalLoaded >= maxResults && (
              <EndStateContainer>
                <EndStateTitle>Showing Top {maxResults} Results</EndStateTitle>
                <EndStateMessage>
                  Can't find what you're looking for? Try refining your search with more specific keywords or filters to get better results.
                </EndStateMessage>
                <RefineSearchButton onClick={() => {
                  // Clear search and scroll to top
                  setSearchQuery('');
                  setSelectedTags([]);
                  setType('all');
                  setDuration('all');
                  setHasSearched(false);
                  setSearchResults([]);
                  updateURLParams('', 'all', 'all', 'date_desc', []);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}>
                  Refine Search
                </RefineSearchButton>
              </EndStateContainer>
            )} */}

            {/* No more results state */}
            {/* {!hasMore && totalLoaded < maxResults && totalLoaded > 0 && (
              <EndStateContainer>
                <EndStateTitle>End of Results</EndStateTitle>
                <EndStateMessage>
                  You've seen all {totalLoaded} available results. Try adjusting your search criteria to find more meditations.
                </EndStateMessage>
              </EndStateContainer>
            )} */}
          </>
        )}
      </ResultsSection>
    </Container>
    </>
  );
};

export default Search; 